/**
 * The UI-level half of the permission gate.
 *
 * FiveOX ADR-0029: **enforce at UI level and API level.** A hidden button is
 * not a permission check — but an enforced API with no hidden button is merely
 * rude, and it teaches users the product is broken. Both, always.
 *
 * **It reads `GET /api/v1/me/access` on our own service, not access-management
 * directly.** That is not a shortcut: the platform edge answers
 * `/api/access/internal/` with a hardcoded 404, and the two member-facing
 * routes that do exist are staff-only and org-admin-only respectively. Our
 * service hands back the same evaluation object it enforces with, so the UI
 * gate cannot drift from the API gate. The full story is in
 * `services/tdsreco-service/app/api/me.py`.
 *
 * It **fails closed**: any failure yields an empty verb map, which renders as
 * the refusal screen rather than as a working app that 403s on every click.
 */

import { useCallback, useEffect, useState } from 'react'

import { ApiError, api, getOrgId } from './api'
import { session } from './session'

/** The five verbs access-management understands. Not extensible by us. */
export type Verb = 'can_view' | 'can_view_all' | 'can_create' | 'can_update' | 'can_delete'

/** The resources TDS Reco publishes. Mirrors `services/tdsreco-service/app/rbac.py`. */
export const CLIENT = 'client'
export const DOCUMENT = 'document'
export const SETTINGS = 'settings'

export interface Evaluation {
  /** resource → verb → allowed. The shape access-management actually returns. */
  permissions: Record<string, Partial<Record<Verb, boolean>>>
  entitled: boolean
  /** Distinguishes "never assigned" from "subscription suspended" from
   *  "app-mgmt unreachable" — the question a support advisor is actually
   *  asked. Surface it wherever a human must understand a refusal. */
  entitlementReason: string
  knownResources: string[]
}

export const DENIED: Evaluation = {
  permissions: {},
  entitled: false,
  entitlementReason: 'authority_unreachable',
  knownResources: [],
}

export async function fetchAccess(): Promise<Evaluation> {
  try {
    const body = await api<{
      permissions: Record<string, Partial<Record<Verb, boolean>>>
      entitled: boolean
      entitlement_reason: string
      known_resources: string[]
    }>('/api/v1/me/access')
    return {
      permissions: body.data.permissions ?? {},
      entitled: Boolean(body.data.entitled),
      entitlementReason: body.data.entitlement_reason ?? 'unknown',
      knownResources: body.data.known_resources ?? [],
    }
  } catch (err) {
    // **Not every failure is an outage, and saying so cost real time.**
    //
    // This used to collapse every error into `authority_unreachable`, so when
    // our own client sent a malformed request the screen announced that the
    // permission service was down. It was not: the service answered 400,
    // promptly and correctly. A wrong diagnosis pointing at someone else's
    // component is worse than a vague one, because somebody goes and looks
    // there.
    if (err instanceof ApiError) {
      // A refusal *about entitlement* is a real answer, and its reason is the
      // whole point of the call.
      if (err.entitlementReason) {
        return { ...DENIED, entitlementReason: err.entitlementReason }
      }
      // 4xx without an entitlement reason is our own bug — a missing header, a
      // bad parameter. Name it as ours.
      if (err.status >= 400 && err.status < 500) {
        console.error('[tds-reco] access read refused by our own service', err.code, err.message)
        return { ...DENIED, entitlementReason: 'client_error' }
      }
    }
    // Anything else — a 5xx, a dropped connection — genuinely is the authority
    // being unavailable.
    return { ...DENIED, entitlementReason: 'authority_unreachable' }
  }
}

export function useEvaluation(): {
  evaluation: Evaluation | null
  loading: boolean
  can: (resource: string, verb: Verb) => boolean
} {
  const current = session.useSession()
  const orgId = getOrgId()
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    if (!orgId || !current) {
      setEvaluation(null)
      setLoading(false)
      return
    }
    setLoading(true)
    fetchAccess().then((result) => {
      if (cancelled) return
      setEvaluation(result)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [orgId, current?.userId])

  const can = useCallback(
    (resource: string, verb: Verb) => Boolean(evaluation?.permissions[resource]?.[verb]),
    [evaluation],
  )

  return { evaluation, loading, can }
}

/**
 * Human wording for a refusal, keyed on `entitlement_reason`.
 *
 * A lapse **suppresses the surface and deletes nothing** (ADR-0042 §2) — so the
 * copy says access is paused, never that the data is gone. That is both true
 * and the difference between a renewal and a panicked support call.
 */
export function refusalCopy(reason: string): { title: string; body: string; cta?: string } {
  switch (reason) {
    case 'not_entitled':
    case 'never_assigned':
    // `no_subscription` is what the live access-service actually answers for an
    // org that has never bought the app — observed 2026-08-25 against the real
    // stack, not guessed. Without this case it fell through to the default and
    // told a prospective customer to "ask an administrator", which is both
    // wrong and a dead end: there is nothing an administrator can do.
    case 'no_subscription':
      return {
        title: 'TDS Reco is not part of this subscription',
        body: 'Your organisation has not added TDS Reco yet. Adding it turns the app on for the people who already have access.',
        cta: 'store',
      }
    case 'subscription_suspended':
    case 'subscription_lapsed':
      return {
        title: 'Access is paused',
        body: 'This organisation’s TDS Reco subscription has lapsed. Your clients, runs and documents are all still here — renewing restores access exactly as it was.',
        cta: 'store',
      }
    case 'app_killed':
    case 'app_maintenance':
      return {
        title: 'TDS Reco is temporarily unavailable',
        body: 'The app is in maintenance. This is not something wrong with your account.',
      }
    case 'authority_unreachable':
      return {
        title: 'We can’t confirm your access right now',
        body: 'The permission service did not answer. This is usually brief — please try again in a moment.',
      }
    case 'client_error':
      // Deliberately blames us. The alternative — telling the customer a
      // platform service is down when the bug is in this app — sends them to
      // support with a question nobody there can answer.
      return {
        title: 'Something went wrong on our side',
        body: 'TDS Reco could not read your permissions, and that is a fault in the app rather than in your account or your subscription. Reloading sometimes clears it; if it does not, please tell us.',
      }
    default:
      return {
        title: 'You don’t have access to TDS Reco',
        body: 'Ask an administrator in your organisation to give you a TDS Reco role.',
        cta: 'console',
      }
  }
}
